import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Node } from '@xyflow/react';
import { ActorData, NetworkZone } from '../types/diagram.types';

interface AddActorModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (actor: Node<ActorData>) => void;
}

const ACTOR_TYPE_OPTIONS: { value: ActorData['actorType']; label: string }[] = [
  { value: 'psp',          label: 'PSP (Payment System)' },
  { value: 'compliance',   label: 'Compliance / KYC / AML' },
  { value: 'blockchain',   label: 'Blockchain Node' },
  { value: 'notification', label: 'Notification Service' },
  { value: 'lp',           label: 'Liquidity Provider' },
  { value: 'client',       label: 'Client / App' },
];

const ZONE_OPTIONS: { value: NetworkZone; label: string }[] = [
  { value: 'zone-partners', label: 'Partners (Z4)' },
  { value: 'zone-external', label: 'External (Z1)' },
];

const DEFAULT_POSITIONS: Record<NetworkZone, { x: number; y: number }> = {
  'zone-partners': { x: 1300, y: 1410 },
  'zone-external': { x: 1900, y: 60 },
  'zone-dmz':      { x: 1900, y: 300 },
  'zone-kafka':    { x: 1900, y: 490 },
  'zone-internal': { x: 1900, y: 700 },
};

const AddActorModal: React.FC<AddActorModalProps> = ({ show, onHide, onAdd }) => {
  const [name, setName] = useState('');
  const [actorType, setActorType] = useState<ActorData['actorType']>('psp');
  const [zone, setZone] = useState<NetworkZone>('zone-partners');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const id = `custom-act-${Date.now()}`;
    const pos = DEFAULT_POSITIONS[zone] ?? { x: 1300, y: 1410 };
    const actor: Node<ActorData> = {
      id,
      type: 'actor',
      position: { x: pos.x + Math.floor(Math.random() * 120), y: pos.y },
      data: {
        label: name.trim(),
        name: name.trim(),
        actorType,
        zone,
        ...(url.trim() ? { url: url.trim() } : {}),
      },
    };
    onAdd(actor);
    // Reset form
    setName('');
    setActorType('psp');
    setZone('zone-partners');
    setUrl('');
    setError('');
  };

  const handleHide = () => {
    setName('');
    setActorType('psp');
    setZone('zone-partners');
    setUrl('');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>Add Partner Service</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Name *</Form.Label>
            <Form.Control
              size="sm"
              placeholder="e.g. Stripe PSP"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              isInvalid={!!error}
            />
            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Type</Form.Label>
            <Form.Select
              size="sm"
              value={actorType}
              onChange={e => setActorType(e.target.value as ActorData['actorType'])}
            >
              {ACTOR_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Zone</Form.Label>
            <Form.Select
              size="sm"
              value={zone}
              onChange={e => setZone(e.target.value as NetworkZone)}
            >
              {ZONE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>URL (optional)</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={handleHide}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddActorModal;
